<template>
  <div class="results-step">
    <div class="results-content">
      <!-- Title -->
      <div class="title-section">
        <h2 class="step-title">{{ step.title }}</h2>
        <p v-if="step.subtitle" class="step-subtitle">{{ step.subtitle }}</p>
      </div>

      <!-- Brand and Measurement Selector -->
      <div class="selectors-section">
        <div class="selector-group">
          <label class="selector-label">Бренд:</label>
          <select v-model="selectedBrand" class="brand-selector">
            <option
              v-for="brand in availableBrands"
              :key="brand.value"
              :value="brand.value"
            >
              {{ brand.label }}
            </option>
          </select>
        </div>

        <div class="selector-group">
          <label class="selector-label">Система измерения:</label>
          <select v-model="selectedSystem" class="system-selector">
            <option
              v-for="system in availableSystems"
              :key="system.value"
              :value="system.value"
            >
              {{ system.label }}
            </option>
          </select>
        </div>
      </div>

      <!-- Loading Indicator -->
      <div v-if="isLoadingResults" class="loading-results">
        <div class="loading-spinner"></div>
        <p>Загрузка результатов...</p>
      </div>

      <!-- Results Categories -->
      <div v-else-if="categories.length > 0" class="results-categories">
        <div v-for="category in categories" :key="category.key" class="category-section">
          <div class="category-card">
            <img
              :src="category.image"
              :alt="category.title"
              class="category-image"
            />
            <div class="category-info">
              <h3 class="category-title">{{ category.title }}</h3>
              <p class="category-subtitle">{{ category.subtitle }}</p>
              <div class="size-selector">
                <label>Размер:</label>
                <select v-model="selectedSizes[category.key]" class="size-select">
                  <option
                    v-for="size in category.sizes"
                    :key="size"
                    :value="size"
                  >
                    {{ size }}
                  </option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- No Results Message -->
      <div v-else class="no-results">
        <p>Результаты пока не загружены. Пожалуйста, завершите опрос.</p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useSurveyModal } from '@/features/survey/store'
import { fetchSurveyResults } from '@/features/survey/api/survey'
import { primarySize } from '@/features/sizes/suggested-size'
import type { SurveyStep } from '@/features/survey/types/survey-config'
import type { CategoryKey, BrandOption, SystemOption } from '@/features/survey/store/useSurveyModalStore'

interface Props {
  step: SurveyStep & { type: 'results' }
}

const props = defineProps<Props>()
const surveyStore = useSurveyModal()
const router = useRouter()
const selectedBrand = ref('all')
const selectedSystem = ref('metric')
const isLoadingResults = ref(false)

// Получаем доступные опции из результатов
const availableBrands = computed<BrandOption[]>(() => {
  const results = surveyStore.surveyModal.results
  if (results?.brands && results.brands.length > 0) {
    return results.brands
  }
  // Fallback значения по умолчанию
  return [
    { value: 'all', label: 'Все бренды' },
    { value: 'zara', label: 'Zara' },
    { value: 'hm', label: 'H&M' },
    { value: 'uniqlo', label: 'Uniqlo' },
    { value: 'nike', label: 'Nike' }
  ]
})

const availableSystems = computed<SystemOption[]>(() => {
  const results = surveyStore.surveyModal.results
  if (results?.systems && results.systems.length > 0) {
    return results.systems
  }
  // Fallback значения по умолчанию
  return [
    { value: 'metric', label: 'Метрическая' },
    { value: 'us', label: 'US' },
    { value: 'eu', label: 'EU' }
  ]
})

// Хранилище для выбранных размеров по категориям
const selectedSizes = ref<Record<CategoryKey, string>>({
  tops: '',
  bottoms: '',
  dresses: '',
  outerwear: '',
  lingerie: '',
  shoes: ''
})

const CATEGORY_KEYS = ['tops', 'bottoms', 'dresses', 'outerwear', 'lingerie', 'shoes'] as const

/**
 * Кандидаты на предвыбор: sizing v2 suggestedSize, затем легаси-массив.
 * Рантайм-ключ легаси-массива после кейс-трансформера — suggestedSizes,
 * в типе он объявлен как suggested_sizes — читаем оба (reader tolerance).
 */
function suggestedCandidates(item: {
  suggestedSize?: Parameters<typeof primarySize>[0]
  suggested_sizes?: string[]
  suggestedSizes?: string[]
}): string[] {
  const v2 = primarySize(item.suggestedSize)
  const legacy = item.suggested_sizes ?? item.suggestedSizes ?? []
  return v2 ? [v2, ...legacy] : [...legacy]
}

interface CategoryDisplay {
  key: CategoryKey
  image: string
  title: string
  subtitle: string
  sizes: string[]
}

// Преобразуем данные из store в формат для отображения
const categories = computed<CategoryDisplay[]>(() => {
  const rawResults = surveyStore.surveyModal.results
  if (!rawResults) {
    return []
  }

  return CATEGORY_KEYS.map((key) => {
    const item = rawResults[key]
    if (!item) {
      return {
        key,
        image: '',
        title: key.charAt(0).toUpperCase() + key.slice(1),
        subtitle: '',
        sizes: []
      }
    }

    const availableSizes = item.sizes || []

    // Если выбранный размер не установлен или не доступен — предвыбираем
    // рекомендованный (v2 suggestedSize, затем легаси), иначе первый доступный
    if (!selectedSizes.value[key] || !availableSizes.includes(selectedSizes.value[key])) {
      const preferred = suggestedCandidates(item).find((size) => availableSizes.includes(size))
      selectedSizes.value[key] = preferred ?? availableSizes[0] ?? ''
    }

    return {
      key,
      image: item.image,
      title: item.title,
      subtitle: item.description,
      sizes: availableSizes
    }
  })
})

// Инициализируем выбранные размеры из suggested_sizes при загрузке результатов
watch(
  () => surveyStore.surveyModal.results,
  (results) => {
    if (results) {
      CATEGORY_KEYS.forEach((key) => {
        const item = results[key]
        if (item) {
          const availableSizes = item.sizes || []

          // Если выбранный размер не установлен — инициализируем рекомендованным
          if (!selectedSizes.value[key] || !availableSizes.includes(selectedSizes.value[key])) {
            const preferred = suggestedCandidates(item).find((size) => availableSizes.includes(size))
            const fallback = preferred ?? availableSizes[0]
            if (fallback) {
              selectedSizes.value[key] = fallback
            }
          }
        }
      })
    }
  },
  { immediate: true }
)

const loadResults = async () => {
  isLoadingResults.value = true
  try {
    const surveyResponseId = surveyStore.surveyModal.surveyResponseId
    if (!surveyResponseId) {
      // Если результатов еще нет, пытаемся отправить опрос
      if (surveyStore.surveyModal.config) {
        await surveyStore.submitSurvey()
        // После отправки surveyResponseId должен быть установлен
        const newResponseId = surveyStore.surveyModal.surveyResponseId
        if (newResponseId) {
          const results = await fetchSurveyResults({
            surveyResponseId: newResponseId,
            brand: selectedBrand.value,
            system: selectedSystem.value
          })
          surveyStore.surveyModal.results = results
        }
      }
      return
    }

    // Запрашиваем данные результатов с бекенда с параметрами бренда и системы измерения
    const results = await fetchSurveyResults({
      surveyResponseId,
      brand: selectedBrand.value,
      system: selectedSystem.value
    })
    
    // Сохраняем результаты в store
    surveyStore.surveyModal.results = results
  } catch (error) {
    console.error('Error loading survey results:', error)
  } finally {
    isLoadingResults.value = false
  }
}

// Отслеживаем изменения бренда и системы измерения
watch([selectedBrand, selectedSystem], () => {
  // При изменении бренда или системы измерения делаем новый запрос
  if (surveyStore.surveyModal.surveyResponseId) {
    loadResults()
  }
})

onMounted(() => {
  // Загружаем результаты при монтировании компонента
  loadResults()
})
</script>

<style scoped>
.results-step {
  padding: 20px;
  max-height: 80vh;
  overflow-y: auto;
}

.results-content {
  max-width: 800px;
  margin: 0 auto;
}

.title-section {
  margin-bottom: 24px;
  text-align: center;
}

.step-title {
  font-size: 24px;
  font-weight: 600;
  color: #1d1d1f;
  margin: 0 0 12px 0;
}

.step-subtitle {
  font-size: 16px;
  color: #666;
  line-height: 1.5;
  margin: 0;
}

.selectors-section {
  display: flex;
  gap: 20px;
  margin-bottom: 32px;
  justify-content: center;
}

.selector-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.selector-label {
  font-size: 14px;
  font-weight: 600;
  color: #1d1d1f;
}

.brand-selector,
.system-selector {
  padding: 8px 12px;
  border: 1px solid #e5e5e7;
  border-radius: 8px;
  font-size: 14px;
  min-width: 150px;
}

.loading-results {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  gap: 16px;
  color: #666;
}

.loading-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #007aff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.results-categories {
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 32px;
}

.category-section {
  width: 100%;
}

.category-card {
  display: flex;
  gap: 20px;
  padding: 20px;
  border: 1px solid #e5e5e7;
  border-radius: 12px;
  background: #fff;
}

.category-image {
  width: 100px;
  height: 100px;
  object-fit: cover;
  border-radius: 8px;
}

.category-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.category-title {
  font-size: 18px;
  font-weight: 600;
  color: #1d1d1f;
  margin: 0;
}

.category-subtitle {
  font-size: 14px;
  color: #666;
  margin: 0;
}

.size-selector {
  display: flex;
  align-items: center;
  gap: 12px;
}

.size-selector label {
  font-size: 14px;
  font-weight: 600;
  color: #1d1d1f;
}

.size-select {
  padding: 8px 12px;
  border: 1px solid #e5e5e7;
  border-radius: 8px;
  font-size: 14px;
  min-width: 100px;
}

.no-results {
  text-align: center;
  padding: 40px 20px;
  color: #666;
}

@media (max-width: 768px) {
  .category-card {
    flex-direction: column;
  }

  .category-image {
    width: 100%;
    height: 200px;
  }

  .selectors-section {
    flex-direction: column;
  }
}
</style>

